import {_Sitemap} from '@localization/siteMap/breadcrumbs/_Sitemap.ts';
import {IRecurso} from "@models/recurso/IRecurso";
import {OpenInNew, SaveOutlined} from "@mui/icons-material";
import {Button, Grid, IconButton, TableCell, TableRow, TextField, ToggleButton, Typography,} from "@mui/material";
import {TextFieldNav, TextFieldNavProps} from '@shared/custom/TextFieldNav.tsx';
import {ToDateDefault, ToMoney} from "@shared/MathHelp";
import {createSearchParams, useNavigate} from "react-router";
import React, {useEffect, useMemo, useState} from "react";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {UpdateRecurso} from "@localization/siteMap/endpoints/_RecursosEndpoints.ts";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {useGeneral} from "@contexts/GeneralContext.tsx";
import {DefaultInfoV1Props} from "@models/DefaultProps.ts";
import {UsuarioInfoNavLink, UsuarioInformacao} from "@components/contas/UsuarioInformacao.tsx";
import {useContextMenu} from "@contexts/hooks/useContextMenu.ts";
import {RenameRecursoComponent} from "@components/recurso/RenameRecursoComponent.tsx";
import {ConfirmationComponent} from "@components/shared/ConfirmationComponent.tsx";
import {useRecurso} from "@components/recurso/hooks/useRecurso.ts";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {NavButton, NavButtonProps} from "@shared/custom/NavButton.tsx";
import {useAuth} from "@contexts/hooks/UseAuth.tsx";
import {IRecursoDetalhesDto} from "@components/recurso/models/dto/IRecursoDetalhesDto.ts";
import lodash from "lodash";

export interface RecursoInformacaoProps extends DefaultInfoV1Props<IRecurso> {
	variant: 'grid' | 'list' | 'navigationLink' | 'row' | 'textfield',
	EndComponent?: (r: IRecurso) => React.ReactNode;
	onUpdate?: (recurso: IRecurso) => void;
}

export function RecursoInformacao(props: RecursoInformacaoProps) {
	const {variant} = props;

	if (variant == "list") return <RecursoInformacaoList {...props}/>;
	if (variant == 'navigationLink') return <RecursoInformacaoNavLink {...props} value={props.value as IRecurso}/>;
	if (variant == 'row') return <RecursoInformacaoRow {...props} />;

	return <RecursoInformacaoGrid {...props} readonly/>;

}

export function RecursoInformacaoNavLink(props: Omit<RecursoInformacaoProps, 'EndComponent' | 'variant' | 'overrides'> & NavButtonProps<IRecurso>) {
	const {setContextOptions} = useContextMenu()
	const {addPopUp, closePopUp} = useAlert()
	const {hasAccess} = useAuth();
	const {updateRecurso} = useRecurso()

	const [value] = useState<IRecurso | undefined>(props.value);

	const inPeriod = useMemo(() => {
		return dayjs().isAfter(value?.dataInicio) && dayjs().isAfter(value?.dataFim)
	}, [value?.dataInicio, value?.dataFim])

	const handleCtx = (e: React.MouseEvent<HTMLElement>) => {
		if (value == null) return
		setContextOptions({
			options: [{
				label: 'Renomear',
				onClick: () => {
					addPopUp({
						size: "md",
						title: "Renomear Recurso",
						content: <RenameRecursoComponent recurso={props.value} onUpdate={(recurso) => {
							if (props.onUpdate != null)
								props.onUpdate(recurso);
							closePopUp()
						}}/>
					})
				},
				disabled: !hasAccess(['EditarRecurso'])
			},
				{
					label: value.isAtivo ? 'Desativar' : 'Ativar', onClick: () => {
						addPopUp({
							size: 'md',
							title: 'Ativar ou desativar recurso',
							content: <ConfirmationComponent options={[
								{
									label: value.isAtivo ? 'Desativar' : 'ativar', onClick: () => {
										updateRecurso({...value, isAtivo: !value.isAtivo}).then(x => {
											if (props.onUpdate != null)
												props.onUpdate(x);
											closePopUp()
										})
									}
								}
							]}>Deseja realmente {value.isAtivo ? 'desativar' : 'ativar'} o
								recurso {value.nome}?</ConfirmationComponent>
						})
					}
				}
			], anchor: e.currentTarget,
			event: e
		})
	}


	return (value && <NavButton<IRecurso>
		{...props}
		label={(v) => v.nome}
		pathname={'/recurso/detalhes'}
		onContextMenu={handleCtx}
		hint={"Ir para a pagina de detalhes do recurso " + value.nome}
		onClick={props.onClick}
		slotProps={{
			chip: {
				...props.slotProps?.chip,
				color: value.isAtivo ? 'primary' : 'default',
				variant: inPeriod ? 'outlined' : 'filled',
			},
		}}
		searchData={{
			recursoId: value.id.toString(),
		}}/>);
}

interface RecursoInformacaoAlteravelProps extends RecursoInformacaoProps {
	readonly?: boolean,
	onUpdate?: (recurso: IRecurso) => void
}

export function RecursoInformacaoRow(props: Omit<RecursoInformacaoProps, 'variant' | 'overrides'> & {
	onUpdate?: (v: IRecurso) => void
}) {
	const {EndComponent, onUpdate} = props;
	const {hasAccess} = useAuth();
	const {screenSizeLevel} = useGeneral();
	const [value] = useState<IRecurso | undefined>(props.value);


	return (value &&
		<TableRow>
			<TableCell>
				<Typography>{value.id}</Typography>
			</TableCell>
			<TableCell>
				<RecursoInformacaoNavLink value={value} tableMode onUpdate={onUpdate}/>
			</TableCell>
			{screenSizeLevel < 4 && <TableCell>
				{value.usuarioCriacao && <UsuarioInfoNavLink value={value.usuarioCriacao} slotProps={{
					chip: {tableMode: true},
				}}/> || <UsuarioInformacao variant={'navigationLink'} slotProps={{
					chip: {tableMode: true}
				}} usuarioId={value.usuarioCriacaoId}/>}
			</TableCell>}
			{screenSizeLevel < 4 && <TableCell align={'right'}>
				{ToDateDefault(value.dataCriacao)}
			</TableCell>}
			<TableCell align={'right'}>
				{hasAccess(['VisualizarRecursoInformacoes']) && ToMoney(value.valor)}
			</TableCell>
			{screenSizeLevel < 6 && <TableCell>
				{ToMoney(value.valorEfetivado)} / {ToMoney(value.valorPendente)}
			</TableCell>}
			<TableCell>
				{EndComponent && <EndComponent {...value}/>}
			</TableCell>
		</TableRow>
	);
}


export function RecursoInformacaoGrid(props: RecursoInformacaoAlteravelProps) {
	const {
		readonly,
		onUpdate,
	} = props
	const [value, setValue] = useState<IRecurso | undefined>(props.value);
	const {createAlertFromResponse} = useAlert();
	const {Get} = useApi();
	const {getDetalhes} = useRecurso()
	const [recursoDetalhes, setRecursoDetalhes] = useState<IRecursoDetalhesDto>();

	const hasChanges = useMemo(() => {
		const v = lodash.pick(value, ['nome', 'descricao', 'isAtivo', 'dataInicio', 'dataFim']);

		return !lodash.isEqual(v, props.value)
	}, [value, props.value])

	useEffect(() => {
		if (value != null) {
			getDetalhes({
				data: {
					recursoId: value.id,
				}
			}).then(x => {
				if (x.items) {
					setRecursoDetalhes(x.items)
				}
			})
		}
	}, [value])
	;
	const navigate = useNavigate();


	useEffect(() => {
		setValue(props.value);
	}, [props.value]);


	const salvarHandler = () => {
		if (value == null) return;
		Get(UpdateRecurso(value)).then(x => {
			createAlertFromResponse(x);
			if (x.status == 'Success' && onUpdate) {
				onUpdate(x.items);
			}
		});
	};


	return (value &&
		<Grid container spacing={1}>
			<Grid size={12}>
				<Grid container justifyContent={'end'}>
					<Grid size={12}>
						<Grid container justifyContent={'end'} spacing={1}>
							<Grid size={3}>
								<ToggleButton selected
								              value={true}
								              color={(value.isAtivo ? 'success' : 'error')}
								              disabled={readonly}
								              onChange={() => {
									              setValue({...value, isAtivo: !value.isAtivo});
								              }}
								              fullWidth
								              sx={{height: '100%'}}>{value.isAtivo ? 'Ativa' : 'Inativa'}{value.isAtivo != value.isAtivo ? '(Alterado)' : ''}</ToggleButton>
							</Grid>
							<Grid size={4}>
								<Button fullWidth
								        sx={{maxHeight: '100%', height: '100%'}}
								        disabled={readonly || !hasChanges}
								        variant={'outlined'}
								        color={'success'}
								        onClick={salvarHandler}
								        endIcon={<SaveOutlined/>}>Salvar</Button>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Grid size={12}>
				<Grid container>
					<Grid size={2}><TextField fullWidth disabled label={'Id'}
					                          variant={'filled'}
					                          value={value.id}/></Grid>
					<Grid size={'grow'}><TextField fullWidth
					                               disabled={readonly}
					                               focused={value.nome != value.nome}
					                               label={'Nome' + (value.nome != value.nome ? '(Alterado)' : '')}
					                               value={value.nome}
					                               variant={'filled'}
					                               onChange={(x) => {
						                               setValue({...value, nome: x.target.value});
					                               }}/>
					</Grid>
				</Grid>
			</Grid>
			<Grid size={12}>
				<Grid size={'grow'}><TextField multiline
				                               fullWidth
				                               disabled={readonly}
				                               label={'Descrição'}
				                               variant={'filled'}

				                               onChange={(x) => {
					                               setValue({...value, descricao: x.target.value});
				                               }}
				                               focused={value.descricao != value.descricao}
				                               maxRows={3}
				                               color={'primary'}
				                               value={value.descricao}/>
				</Grid>
			</Grid>
			<Grid size={12}>
				<Grid container>
					<Grid size={6}>
						{value.usuarioCriacao &&
							<TextField fullWidth
							           variant={'filled'}
							           disabled label={'Criado por'} value={value.usuarioCriacao.nome}/>}
					</Grid>
					<Grid size={6}>
						<TextField fullWidth
						           variant={'filled'}
						           disabled label={'Data de Criação'}
						           value={ToDateDefault(value.dataCriacao)}/></Grid>

				</Grid>
			</Grid>
			<Grid size={12}>
				<Grid container justifyContent={'end'}>
					<Grid size={12}>
						<TextField disabled label={'Recurso base'}
						           variant={'filled'}
						           fullWidth
						           slotProps={
							           {
								           input: {
									           endAdornment: <IconButton onClick={() => {
										           navigate({
											           pathname: '/recurso/detalhes',
											           search: createSearchParams({
												           recursoId: value.antecessor?.id.toString() ?? '',
											           }).toString(),
										           });
									           }}
									                                     disabled={!value.antecessor}><OpenInNew/>
									           </IconButton>,
								           },
							           }}
						           value={value.antecessor?.nome ?? 'Não possui recurso base'}/>
					</Grid>


				</Grid>
			</Grid>
			{recursoDetalhes != null && <><Grid size={12}>
				<Grid container justifyContent={'end'}>
					<Grid size={6}>
						<TextField fullWidth
						           variant={'filled'}
						           label={'Valor Original'}
						           disabled
						           value={ToMoney(recursoDetalhes?.valorOriginal)}/>
					</Grid>
					<Grid size={6}>
						<TextField
							variant={'filled'}
							fullWidth
							disabled
							label={'Saldo Disponível'}
							value={ToMoney(recursoDetalhes?.valorDisponivel)}/></Grid>
				</Grid>
			</Grid>
				<Grid size={12}>
					<Grid container justifyContent={'end'}>
						<Grid size={6}>
							<TextField fullWidth
							           variant={'filled'}
							           label={'Entradas'}
							           disabled
							           value={ToMoney(recursoDetalhes?.valorEntradas)}/>
						</Grid>
						<Grid size={6}>
							<TextField fullWidth
							           variant={'filled'}
							           label={'Saidas'}
							           disabled
							           value={ToMoney(recursoDetalhes?.valorSaidas)}/>
						</Grid>
					</Grid>
				</Grid>
			</>}
			<Grid size={12}>
				<Grid container justifyContent={'end'}>
					<Grid size={6}>
						<DatePicker
							label={'Vigencia inicial' + (dayjs(value.dataInicio).date() != dayjs(value.dataInicio).date() ? '(Alterado)' : '')}
							disabled={readonly}
							value={dayjs(value.dataInicio)}
							localeText={{'todayButtonLabel': 'Hoje'}}
							// maxDate={recursoState.vigenciaFinal ?? dayjs("2999-12-31")}
							slotProps={{
								textField: {
									fullWidth: true,
									color: 'primary',
									focused: dayjs(value.dataInicio).date() != dayjs(value.dataInicio).date(),
									variant: 'filled',
								},
								actionBar: {actions: ['today']},
							}}
							onChange={(x) => {
								setValue({...value, dataInicio: dayjs(x)});
							}}
						/>
					</Grid>
					<Grid size={6}>
						<DatePicker sx={{maxWidth: '100%', width: '100%'}}
						            label={'Vigencia final' + (dayjs(value.dataFim).date() != dayjs(value.dataFim).date() ? '(Alterado)' : '')}
						            value={dayjs(value.dataFim)}
						            disabled={readonly}
						            localeText={{'todayButtonLabel': 'Hoje'}}
							// minDate={recursoState.vigenciaInicial ?? dayjs("1900-01-01")}
							        onChange={(x) => {
								        setValue({...value, dataFim: dayjs(x)});
							        }}
							        slotProps={{
								        textField: {
									        fullWidth: true,
									        color: 'primary',
									        focused: dayjs(value.dataFim).date() != dayjs(value.dataFim).date(),
									        variant: 'filled',
								        },
								        actionBar: {actions: ['today']},
							        }}

						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}

export function RecursoInformacaoList(props: RecursoInformacaoProps) {
	const {EndComponent} = props;
	const [value] = useState<IRecurso | undefined>(props.value);

	return (
		value &&
		<Grid container justifyContent={'space-between'}>
			<Grid size={'grow'}>
				<Grid container spacing={1} alignContent={'center'} height={'100%'}>
					<Grid><Typography>ID: {value.id}</Typography></Grid>
					<Grid><RecursoInformacaoNavLink {...props} value={props.value as IRecurso}/></Grid>
					{value.usuarioCriacao &&
						<Grid>
							<UsuarioInformacao variant={'navigationLink'}
							                   value={value.usuarioCriacao}/>

						</Grid>}
					<Grid>
						<Typography>Em {ToDateDefault(value.dataCriacao)}</Typography>
					</Grid>
				</Grid>
			</Grid>
			{EndComponent && <Grid><EndComponent {...value}/></Grid>}
		</Grid>
	);
}


export function RecursoInfoTextField(props: Omit<RecursoInformacaoProps, 'variant' | 'overrides'> & Partial<Omit<TextFieldNavProps<IRecurso>, 'pathName' | 'searchData'>>) {
	const {displayMode, label} = props;
	const [value] = useState<IRecurso | undefined>(props.value);

	const fieldProps = value && {
		...props,
		pathName: _Sitemap.recurso.children.detalhes.fullUrl,
		searchData: {
			recursoId: value.id.toString(),
		},
		displayMode: displayMode ?? 'nome',
		label: label ?? 'Recurso',
	} as TextFieldNavProps<IRecurso>;
	return (fieldProps &&
		<TextFieldNav {...fieldProps}/>
	);
}